import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, TableWrap, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { dateTime, since } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { Table } from './Reports.tsx';

/**
 * Writing to prospects: the "Write e-mail" dialog (one e-mail per person, with their fields filled
 * in and a preview for each), the list of e-mails sent and waiting, the templates, and how e-mails
 * are sent (sender, limits, hours) with a check of the sender's domain.
 */

export const FIELDS = ['first_name', 'last_name', 'full_name', 'job_title', 'company', 'website', 'industry', 'city', 'country', 'fleet_size', 'sender_name', 'sender_email', 'my_company', 'demo_link'] as const;
type Blocked = 'archived' | 'no_email' | 'no_basis' | 'bounced' | 'unsubscribed' | 'company_closed';
type Check = 'ok' | 'warn' | 'missing';

interface Dns {
  domain: string;
  mx: Check;
  spf: Check;
  spf_record: string | null;
  dkim: Check;
  dkim_selector: string | null;
  dmarc: Check;
  dmarc_record: string | null;
  dmarc_policy: string | null;
}
export interface MailSettings {
  from_name: string | null;
  from_email: string | null;
  reply_to: string | null;
  signature: string | null;
  daily_limit: number;
  hourly_limit: number;
  warmup: boolean;
  window_start: number;
  window_end: number;
  weekdays: number[];
  track_opens: boolean;
  dkim_selector: string | null;
  last_error: string | null;
  dns: Dns | Record<string, never>;
  dns_checked_at: string | null;
  missing: string[];
  sender: { name: string; email: string | null };
  marketing_from: string | null;
  today: { sent: number; cap: number };
  queued: number;
  in_window: boolean;
}
interface Template {
  id: string;
  name: string;
  subject: string;
  body: string;
  active: boolean;
  sent: number;
}
interface Recipient {
  id: string;
  first_name: string;
  last_name: string | null;
  email: string | null;
  job_title: string | null;
  company: string;
  company_name: string;
  blocked: Blocked | null;
}
interface Preview {
  to: { name: string; email: string | null };
  from: { name: string; email: string | null };
  subject: string;
  text: string;
  missing: string[];
  unknown: string[];
  blocked: Blocked | null;
}
type MsgState = 'queued' | 'sent' | 'failed' | 'skipped' | 'cancelled';
interface MessageRow {
  id: string;
  company: string;
  company_name: string;
  contact: string;
  name: string;
  email: string;
  subject: string;
  state: MsgState;
  reason: string | null;
  due_at: string;
  sent_at: string | null;
  opened_at: string | null;
  opens: number;
  unsubscribed_at: string | null;
  created_at: string;
  by_name: string | null;
}
interface MessageFull extends MessageRow {
  body: string;
}

const personName = (p: { first_name: string; last_name: string | null }) => [p.first_name, p.last_name].filter(Boolean).join(' ');
const hour = (h: number) => `${String(h).padStart(2, '0')}:00`;
const DAYS = [1, 2, 3, 4, 5, 6, 7] as const;

function msgLook(m: Pick<MessageRow, 'state' | 'opened_at' | 'unsubscribed_at'>): { tone: Tone; icon: IconName; label: string } {
  if (m.unsubscribed_at) return { tone: 'neutral', icon: 'bellOff', label: t('om.s_unsubscribed') };
  if (m.state === 'sent' && m.opened_at) return { tone: 'ok', icon: 'eye', label: t('om.s_opened') };
  switch (m.state) {
    case 'queued':
      return { tone: 'info', icon: 'clock', label: t('om.s_queued') };
    case 'sent':
      return { tone: 'ok', icon: 'send', label: t('om.s_sent') };
    case 'failed':
      return { tone: 'danger', icon: 'alert', label: t('om.s_failed') };
    case 'cancelled':
      return { tone: 'neutral', icon: 'x', label: t('om.s_cancelled') };
    default:
      return { tone: 'warn', icon: 'minus', label: t('om.s_skipped') };
  }
}

/** Why someone was left out, in words. */
export function reasonText(reason: string | null, fields?: string[]) {
  switch (reason) {
    case 'missing':
      return t('om.r_missing', { fields: (fields ?? []).map((f) => `{${f}}`).join(', ') });
    case 'recent':
      return t('om.r_recent');
    case 'archived':
    case 'no_email':
    case 'no_basis':
    case 'bounced':
    case 'unsubscribed':
    case 'company_closed':
      return t(`out.b_${reason}` as MessageKey);
    case 'changed':
      return t('om.r_changed');
    case 'cancelled':
    case 'manual':
      return t('om.r_cancelled');
    case 'in_sequence':
      return t('sq.r_in_sequence');
    case 'replied':
    case 'interested':
    case 'demo':
    case 'customer':
    case 'closed':
      return t(`sq.why_${reason}` as MessageKey);
    default:
      return reason ?? '—';
  }
}

/** A row of buttons that put a field at the cursor of the subject or the text. */
export function FieldChips({ onPick }: { onPick: (field: string) => void }) {
  return (
    <div className="om-fields" role="group" aria-label={t('om.fields')}>
      <span className="muted small">{t('om.fields')}</span>
      {FIELDS.map((f) => (
        <button key={f} type="button" className="chip" onClick={() => onPick(f)} title={t(`om.f_${f}` as MessageKey)}>
          {`{${f}}`}
        </button>
      ))}
    </div>
  );
}

export function useInsert(value: string, set: (v: string) => void) {
  const ref = useRef<HTMLTextAreaElement & HTMLInputElement>(null);
  const insert = (field: string) => {
    const el = ref.current;
    const token = `{${field}}`;
    const at = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? at;
    set(value.slice(0, at) + token + value.slice(end));
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(at + token.length, at + token.length);
    });
  };
  return { ref, insert };
}

/** What has to be set up before e-mails can go, with the way there. */
export function NotReady({ missing }: { missing: string[] }) {
  const { can } = useSession();
  if (!missing.length) return null;
  return (
    <p className="callout callout-warn">
      <Icon name="alert" size={16} />
      <span>
        {t('om.not_ready', { what: missing.map((m) => t(`om.m_${m}` as MessageKey)).join(', ') })}{' '}
        {can('marketing.settings') ? (
          <Link className="link" to="/marketing?tab=settings">
            {t('om.open_mail_settings')}
          </Link>
        ) : (
          t('om.ask_manager')
        )}
      </span>
    </p>
  );
}

// ---- writing -----------------------------------------------------------------------------------

export function ComposeDialog({ contacts = [], companies = [], onClose, onSent }: { contacts?: string[]; companies?: string[]; onClose: () => void; onSent: () => Promise<void> }) {
  const { data: who } = useAction<{ people: Recipient[]; companies_without_people: number }>('outreach.recipients', { contacts, companies });
  const { data: tpl } = useAction<Template[]>('outreach.templates', {});
  const { data: s } = useAction<MailSettings>('outreach.email_settings', {});
  const [template, setTemplate] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [focus, setFocus] = useState<'subject' | 'body'>('body');
  const [shown, setShown] = useState<string | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [result, setResult] = useState<{ queued: number; skipped: { contact: string; name: string; reason: string; fields?: string[] }[] } | null>(null);
  const [tried, setTried] = useState(false);
  const { busy, run } = useSave();
  const subj = useInsert(subject, setSubject);
  const text = useInsert(body, setBody);
  const people = who?.people ?? [];
  const ready = people.filter((p) => !p.blocked);
  const current = shown ?? ready[0]?.id ?? people[0]?.id ?? null;

  // The preview follows what is typed, for the person shown, a moment after typing stops.
  useEffect(() => {
    if (!current) return;
    const id = setTimeout(() => {
      action<Preview>('outreach.preview', { contact: current, subject, body }).then(setPreview, () => setPreview(null));
    }, 300);
    return () => clearTimeout(id);
  }, [current, subject, body]);

  const pickTemplate = (id: string) => {
    setTemplate(id);
    const x = tpl?.find((y) => y.id === id);
    if (x) {
      setSubject(x.subject);
      setBody(x.body);
    }
  };
  const errors = { subject: !subject.trim() ? t('om.e_subject') : null, body: !body.trim() ? t('om.e_body') : null };
  const send = () => {
    setTried(true);
    if (errors.subject || errors.body || !ready.length) return;
    void run(async () => {
      setResult(await action('outreach.send', { contacts: ready.map((p) => p.id), template: template || null, subject: subject.trim(), body: body.trim() }));
      await onSent();
    });
  };

  if (result) {
    return (
      <Dialog
        title={t('om.sent_title')}
        onClose={onClose}
        size="md"
        footer={
          <Button variant="primary" icon="check" onClick={onClose}>
            {t('om.done')}
          </Button>
        }
      >
        <div className="form-stack">
          <p className={`callout ${result.queued ? 'callout-ok' : 'callout-warn'}`}>
            <Icon name={result.queued ? 'checkCircle' : 'alert'} size={16} />
            <span>{result.queued ? tn('om.queued_n', result.queued) : t('om.queued_none')}</span>
          </p>
          {result.queued && s ? <p className="muted small">{t('om.pace', { from: hour(s.window_start), to: hour(s.window_end), n: s.hourly_limit })}</p> : null}
          {result.skipped.length ? (
            <section aria-labelledby="om-left-out">
              <h3 id="om-left-out" className="om-sub">
                {tn('om.left_out_n', result.skipped.length)}
              </h3>
              <ul className="om-list">
                {result.skipped.map((x) => (
                  <li key={x.contact}>
                    <span className="strong">{x.name || '—'}</span>
                    <span className="muted small">{reasonText(x.reason, x.fields)}</span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      </Dialog>
    );
  }

  const blocked = people.filter((p) => p.blocked);
  return (
    <Dialog
      title={people.length === 1 && people[0] ? t('om.write_to', { name: personName(people[0]) }) : t('om.write_title')}
      onClose={onClose}
      size="xl"
      footer={
        <>
          <span className="foot-actions muted small">{s ? t('om.pace', { from: hour(s.window_start), to: hour(s.window_end), n: s.hourly_limit }) : null}</span>
          <Button variant="primary" icon="send" busy={busy} disabled={!who || !ready.length || !!s?.missing.length} onClick={send}>
            {ready.length === 1 ? t('om.send_one') : tn('om.send_n', ready.length)}
          </Button>
        </>
      }
    >
      {!who || !s ? (
        <Skeleton h={360} />
      ) : (
        <div className="om-compose">
          <div className="form-stack">
            <NotReady missing={s.missing} />
            <p className="om-from muted small">
              <Icon name="mail" size={14} /> {t('om.from_line', { name: s.sender.name, email: s.sender.email ?? '—' })}
            </p>
            {tpl?.length ? (
              <Field label={t('om.template')}>
                <select className="input" value={template} onChange={(e) => pickTemplate(e.target.value)}>
                  <option value="">{t('om.no_template')}</option>
                  {tpl.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
                </select>
              </Field>
            ) : null}
            <Field label={t('om.subject')} required error={tried ? errors.subject : null} counter={`${subject.length}/200`}>
              <input ref={subj.ref} className="input" value={subject} maxLength={200} onFocus={() => setFocus('subject')} onChange={(e) => setSubject(e.target.value)} data-autofocus />
            </Field>
            <Field label={t('om.body')} required error={tried ? errors.body : null} hint={t('om.body_hint')}>
              <textarea ref={text.ref} className="input om-body" rows={12} value={body} maxLength={20000} onFocus={() => setFocus('body')} onChange={(e) => setBody(e.target.value)} />
            </Field>
            <FieldChips onPick={(f) => (focus === 'subject' ? subj.insert(f) : text.insert(f))} />
          </div>
          <aside className="om-side" aria-label={t('om.preview')}>
            <div className="panel-head">
              <h2 className="om-sub">{t('om.preview')}</h2>
              {people.length > 1 ? (
                <select className="input" aria-label={t('om.preview_for')} value={current ?? ''} onChange={(e) => setShown(e.target.value)}>
                  {people.map((p) => (
                    <option key={p.id} value={p.id}>
                      {personName(p)} · {p.company_name}
                    </option>
                  ))}
                </select>
              ) : null}
            </div>
            {!preview ? (
              <Skeleton h={200} />
            ) : (
              <div className="om-preview">
                {preview.blocked ? (
                  <p className="callout callout-warn">
                    <Icon name="bellOff" size={16} />
                    <span>{t('om.preview_blocked', { why: reasonText(preview.blocked) })}</span>
                  </p>
                ) : null}
                {preview.unknown.length ? (
                  <p className="callout callout-danger">
                    <Icon name="alert" size={16} />
                    <span>{t('om.unknown_fields', { fields: preview.unknown.map((f) => `{${f}}`).join(', ') })}</span>
                  </p>
                ) : preview.missing.length ? (
                  <p className="callout callout-warn">
                    <Icon name="info" size={16} />
                    <span>{t('om.missing_fields', { fields: preview.missing.map((f) => `{${f}}`).join(', ') })}</span>
                  </p>
                ) : null}
                <dl className="om-head">
                  <dt>{t('om.to')}</dt>
                  <dd>
                    {preview.to.name} &lt;{preview.to.email ?? '—'}&gt;
                  </dd>
                  <dt>{t('om.subject')}</dt>
                  <dd className="strong">{preview.subject || '—'}</dd>
                </dl>
                <pre className="om-text">{preview.text}</pre>
              </div>
            )}
            <section aria-labelledby="om-who">
              <h3 id="om-who" className="om-sub">
                {tn('om.ready_n', ready.length)}
              </h3>
              {blocked.length || who.companies_without_people ? (
                <ul className="om-list">
                  {blocked.map((p) => (
                    <li key={p.id}>
                      <span className="strong">
                        {personName(p)} · {p.company_name}
                      </span>
                      <span className="muted small">{reasonText(p.blocked)}</span>
                    </li>
                  ))}
                  {who.companies_without_people ? (
                    <li>
                      <span className="muted small">{tn('om.no_people_n', who.companies_without_people)}</span>
                    </li>
                  ) : null}
                </ul>
              ) : (
                <p className="muted small">{t('om.all_ready')}</p>
              )}
            </section>
          </aside>
        </div>
      )}
    </Dialog>
  );
}

// ---- e-mails -----------------------------------------------------------------------------------

const MESSAGE_FILTERS = ['all', 'queued', 'sent', 'opened', 'failed', 'skipped'] as const;
type MessageFilter = (typeof MESSAGE_FILTERS)[number];

export function EmailsTab({ onOpenCompany }: { onOpenCompany: (id: string) => void }) {
  const [filter, setFilter] = useState<MessageFilter>('all');
  const [typed, setTyped] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState<string | null>(null);
  useEffect(() => {
    const id = setTimeout(() => (setSearch(typed.trim()), setOffset(0)), 300);
    return () => clearTimeout(id);
  }, [typed]);
  const { data, reload } = useAction<{ total: number; rows: MessageRow[] }>('outreach.messages', { filter, search: search || null, offset });
  const { data: s } = useAction<MailSettings>('outreach.email_settings', {});
  return (
    <section className="out-section" aria-label={t('out.tab_emails')}>
      {s ? (
        <div className="om-status">
          <NotReady missing={s.missing} />
          {s.last_error ? (
            <p className="callout callout-danger">
              <Icon name="alert" size={16} />
              <span>{t('om.server_error', { error: s.last_error })}</span>
            </p>
          ) : null}
          <p className="muted small">
            <Icon name={s.in_window ? 'send' : 'moon'} size={14} /> {t('om.today', { sent: s.today.sent, cap: s.today.cap })}
            {' · '}
            {tn('om.waiting_n', s.queued)}
            {' · '}
            {s.in_window ? t('om.sending_now') : t('om.outside_hours', { from: hour(s.window_start), to: hour(s.window_end) })}
          </p>
        </div>
      ) : null}
      <div className="out-filters om-filters">
        <div className="segmented out-seg" role="group" aria-label={t('om.filter')}>
          {MESSAGE_FILTERS.map((f) => (
            <button key={f} type="button" aria-pressed={filter === f} className={filter === f ? 'active' : ''} onClick={() => (setFilter(f), setOffset(0))}>
              {t(`om.fl_${f}` as MessageKey)}
            </button>
          ))}
        </div>
        <input className="input" type="search" value={typed} placeholder={t('om.search')} aria-label={t('om.search')} onChange={(e) => setTyped(e.target.value)} />
      </div>
      {!data ? (
        <Skeleton h={240} />
      ) : !data.total && filter === 'all' && !search ? (
        <EmptyState icon="send" title={t('om.empty')} body={t('om.empty_hint')} />
      ) : (
        <>
          <TableWrap label={t('out.tab_emails')}>
            <Table
              view="outreach.messages"
              head={[t('om.c_to'), t('om.c_subject'), t('om.c_state'), t('om.c_when'), t('om.c_by')]}
              phoneHide={[1, 4]}
              left={[0, 1, 2, 4]}
              empty={t('om.none_match')}
              rows={data.rows.map((m) => {
                const look = msgLook(m);
                return {
                  key: m.id,
                  onClick: () => setOpen(m.id),
                  sort: [m.name.toLowerCase(), m.subject.toLowerCase(), m.state, m.sent_at ?? m.due_at, m.by_name?.toLowerCase() ?? null],
                  cells: [
                    <span key="to" className="vat-party">
                      <span className="strong">{m.name}</span>
                      <span className="muted small">{m.company_name}</span>
                    </span>,
                    <span key="s" className="om-subject">
                      {m.subject}
                    </span>,
                    <span key="st" className="vat-party">
                      <StatusBadge tone={look.tone} icon={look.icon} label={look.label} />
                      {m.state === 'skipped' || m.state === 'failed' ? <span className="muted small">{reasonText(m.reason)}</span> : null}
                    </span>,
                    <span key="w" className="muted small">
                      {m.sent_at ? since(m.sent_at) : m.state === 'queued' ? t('om.waiting') : since(m.created_at)}
                    </span>,
                    m.by_name ?? '—',
                  ],
                };
              })}
            />
          </TableWrap>
          <div className="row-actions mkt-pager">
            <span className="muted small">{t('out.showing', { from: data.total ? offset + 1 : 0, to: offset + data.rows.length, total: data.total })}</span>
            <IconButton icon="back" label={t('out.prev')} disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 50))} />
            <IconButton icon="chevronRight" label={t('out.next')} disabled={offset + data.rows.length >= data.total} onClick={() => setOffset(offset + 50)} />
          </div>
        </>
      )}
      {open ? (
        <MessageDialog
          id={open}
          onClose={() => setOpen(null)}
          onCompany={(id) => (setOpen(null), onOpenCompany(id))}
          onChanged={async () => {
            await reload();
          }}
        />
      ) : null}
    </section>
  );
}

export function MessageDialog({ id, onClose, onChanged, onCompany }: { id: string; onClose: () => void; onChanged: () => Promise<void>; onCompany?: (id: string) => void }) {
  const { can } = useSession();
  const { data: m, reload } = useAction<MessageFull>('outreach.message', { id });
  const { busy, run } = useSave();
  if (!m) {
    return (
      <Dialog title={t('common.loading')} onClose={onClose} size="md">
        <Skeleton h={240} />
      </Dialog>
    );
  }
  const look = msgLook(m);
  return (
    <Dialog
      title={m.subject}
      onClose={onClose}
      size="md"
      footer={
        <>
          {onCompany ? (
            <span className="foot-actions">
              <Button variant="ghost" icon="store" onClick={() => onCompany(m.company)}>
                {t('om.open_company', { name: m.company_name })}
              </Button>
            </span>
          ) : null}
          {m.state === 'queued' && can('outreach.send') ? (
            <Button
              variant="secondary"
              icon="x"
              busy={busy}
              onClick={() =>
                void run(async () => {
                  await action('outreach.cancel', { ids: [m.id] });
                  await reload();
                  await onChanged();
                }, t('om.cancelled'))
              }
            >
              {t('om.cancel_send')}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="form-stack">
        <div className="out-head">
          <StatusBadge tone={look.tone} icon={look.icon} label={look.label} />
          {m.reason && m.state !== 'sent' ? <span className="muted small">{reasonText(m.reason)}</span> : null}
        </div>
        <dl className="om-head">
          <dt>{t('om.to')}</dt>
          <dd>
            {m.name} &lt;{m.email}&gt; · {m.company_name}
          </dd>
          <dt>{m.sent_at ? t('om.sent_at') : t('om.due_at')}</dt>
          <dd>{dateTime(m.sent_at ?? m.due_at)}</dd>
          {m.opened_at ? (
            <>
              <dt>{t('om.opened_at')}</dt>
              <dd>
                {dateTime(m.opened_at)} · {tn('om.opens_n', m.opens)}
              </dd>
            </>
          ) : null}
          {m.by_name ? (
            <>
              <dt>{t('om.by')}</dt>
              <dd>{m.by_name}</dd>
            </>
          ) : null}
        </dl>
        <pre className="om-text">{m.body}</pre>
      </div>
    </Dialog>
  );
}

// ---- templates ---------------------------------------------------------------------------------

export function TemplatesTab() {
  const { can } = useSession();
  const manage = can('outreach.manage');
  const [archived, setArchived] = useState(false);
  const { data, reload } = useAction<Template[]>('outreach.templates', { archived });
  const [editing, setEditing] = useState<Template | 'new' | null>(null);
  const { busy, run } = useSave();
  return (
    <section className="out-section" aria-label={t('out.tab_templates')}>
      <div className="out-filters om-filters">
        <p className="muted small">{t('om.templates_intro')}</p>
        <span className="row-actions">
          <label className="out-toggle">
            <Switch checked={archived} onChange={setArchived} label={t('om.show_archived')} />
            <span>{t('om.show_archived')}</span>
          </label>
          {manage ? (
            <Button icon="plus" variant="primary" onClick={() => setEditing('new')}>
              {t('om.new_template')}
            </Button>
          ) : null}
        </span>
      </div>
      {!data ? (
        <Skeleton h={200} />
      ) : !data.length ? (
        <EmptyState
          icon="file"
          title={archived ? t('om.no_archived_templates') : t('om.no_templates')}
          body={archived ? undefined : t('om.no_templates_hint')}
          action={
            manage && !archived ? (
              <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>
                {t('om.new_template')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <ul className="om-templates">
          {data.map((x) => (
            <li key={x.id} className="panel">
              <div className="panel-head">
                <h2 className="om-sub">{x.name}</h2>
                <span className="row-actions">
                  <span className="muted small">{tn('om.used_n', x.sent)}</span>
                  {manage ? (
                    <>
                      <IconButton icon="edit" label={t('om.edit_template', { name: x.name })} onClick={() => setEditing(x)} />
                      <IconButton
                        icon={x.active ? 'archive' : 'undo'}
                        label={x.active ? t('om.archive_template', { name: x.name }) : t('om.restore_template', { name: x.name })}
                        disabled={busy}
                        onClick={() =>
                          void run(
                            async () => {
                              await action('outreach.archive_template', { id: x.id, active: !x.active });
                              await reload();
                            },
                            x.active ? t('om.template_archived') : t('om.template_restored'),
                          )
                        }
                      />
                    </>
                  ) : null}
                </span>
              </div>
              <p className="strong">{x.subject}</p>
              <p className="muted om-snippet">{x.body}</p>
            </li>
          ))}
        </ul>
      )}
      {editing ? (
        <TemplateForm
          template={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await reload();
          }}
        />
      ) : null}
    </section>
  );
}

function TemplateForm({ template, onClose, onSaved }: { template: Template | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const [name, setName] = useState(template?.name ?? '');
  const [subject, setSubject] = useState(template?.subject ?? '');
  const [body, setBody] = useState(template?.body ?? '');
  const [focus, setFocus] = useState<'subject' | 'body'>('body');
  const [tried, setTried] = useState(false);
  const { busy, run } = useSave();
  const subj = useInsert(subject, setSubject);
  const text = useInsert(body, setBody);
  const unknown = useMemo(() => {
    const out = new Set<string>();
    for (const m of `${subject}\n${body}`.matchAll(/\{\s*([a-zA-Z_]+)\s*(?:\|[^{}]*)?\}/g)) if (!(FIELDS as readonly string[]).includes(m[1]!.toLowerCase())) out.add(m[1]!);
    return [...out];
  }, [subject, body]);
  const errors = { name: !name.trim() ? t('om.e_name') : null, subject: !subject.trim() ? t('om.e_subject') : null, body: !body.trim() ? t('om.e_body') : null };
  const save = () => {
    setTried(true);
    if (errors.name || errors.subject || errors.body || unknown.length) return;
    void run(
      async () => {
        await action('outreach.save_template', { id: template?.id, name: name.trim(), subject: subject.trim(), body: body.trim() });
        await onSaved();
      },
      template ? t('om.template_saved') : t('om.template_added'),
    );
  };
  return (
    <Dialog
      title={template ? t('om.edit_template', { name: template.name }) : t('om.new_template')}
      onClose={onClose}
      size="lg"
      footer={
        <Button variant="primary" icon="check" busy={busy} onClick={save}>
          {template ? t('common.save') : t('om.add_template')}
        </Button>
      }
    >
      <div className="form-stack">
        <Field label={t('om.template_name')} required error={tried ? errors.name : null} hint={t('om.template_name_hint')}>
          <input className="input" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} data-autofocus />
        </Field>
        <Field label={t('om.subject')} required error={tried ? errors.subject : null} counter={`${subject.length}/200`}>
          <input ref={subj.ref} className="input" value={subject} maxLength={200} onFocus={() => setFocus('subject')} onChange={(e) => setSubject(e.target.value)} />
        </Field>
        <Field label={t('om.body')} required error={tried ? errors.body : null} hint={t('om.body_hint')}>
          <textarea ref={text.ref} className="input om-body" rows={12} value={body} maxLength={20000} onFocus={() => setFocus('body')} onChange={(e) => setBody(e.target.value)} />
        </Field>
        <FieldChips onPick={(f) => (focus === 'subject' ? subj.insert(f) : text.insert(f))} />
        {unknown.length ? (
          <p className="callout callout-danger" role="alert">
            <Icon name="alert" size={16} />
            <span>{t('om.unknown_fields', { fields: unknown.map((f) => `{${f}}`).join(', ') })}</span>
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

// ---- how e-mails are sent ----------------------------------------------------------------------

const CHECK_LOOK: Record<Check, { tone: Tone; icon: IconName }> = {
  ok: { tone: 'ok', icon: 'checkCircle' },
  warn: { tone: 'warn', icon: 'alert' },
  missing: { tone: 'danger', icon: 'x' },
};

function DnsRow({ name, check, detail, advice }: { name: string; check: Check; detail: string | null; advice: string }) {
  const look = CHECK_LOOK[check];
  return (
    <li>
      <span className="vat-party">
        <span className="strong">{name}</span>
        {detail ? <code className="om-record">{detail}</code> : null}
        {check !== 'ok' ? <span className="muted small">{advice}</span> : null}
      </span>
      <StatusBadge tone={look.tone} icon={look.icon} label={t(`om.dns_${check}` as MessageKey)} />
    </li>
  );
}

export function SendingSettings({ onClose, onSaved }: { onClose: () => void; onSaved: () => Promise<void> }) {
  const { can } = useSession();
  const edit = can('outreach.settings');
  const { data: s, reload } = useAction<MailSettings>('outreach.email_settings', {});
  const [f, setF] = useState<Omit<MailSettings, 'missing' | 'sender' | 'marketing_from' | 'today' | 'queued' | 'in_window' | 'last_error' | 'dns' | 'dns_checked_at'> | null>(null);
  const [dns, setDns] = useState<(Dns & { checked_at?: string }) | null>(null);
  const [checking, setChecking] = useState(false);
  const { busy, run } = useSave();
  useEffect(() => {
    if (!s || f) return;
    const { missing, sender, marketing_from, today, queued, in_window, last_error, dns: d, dns_checked_at, ...rest } = s;
    setF(rest);
    if ('domain' in d) setDns({ ...(d as Dns), checked_at: dns_checked_at ?? undefined });
  }, [s, f]);
  const check = async () => {
    setChecking(true);
    try {
      setDns(await action('outreach.check_domain', {}));
    } catch {
      setDns(null);
    } finally {
      setChecking(false);
    }
  };
  const up = <K extends keyof NonNullable<typeof f>>(k: K, v: NonNullable<typeof f>[K]) => setF((x) => (x ? { ...x, [k]: v } : x));
  const save = () => {
    if (!f) return;
    void run(async () => {
      await action('outreach.save_email_settings', {
        ...f,
        from_name: f.from_name?.trim() || null,
        from_email: f.from_email?.trim() || null,
        reply_to: f.reply_to?.trim() || null,
        signature: f.signature?.trim() || null,
        dkim_selector: f.dkim_selector?.trim() || null,
      });
      await reload();
      await onSaved();
      onClose();
    }, t('om.settings_saved'));
  };
  const hoursBad = f ? f.window_end <= f.window_start : false;
  return (
    <Dialog
      title={t('om.settings_title')}
      onClose={onClose}
      size="lg"
      footer={
        edit ? (
          <Button variant="primary" icon="check" busy={busy} disabled={!f || hoursBad || !f.weekdays.length} onClick={save}>
            {t('common.save')}
          </Button>
        ) : undefined
      }
    >
      {!s || !f ? (
        <Skeleton h={420} />
      ) : (
        <div className="form-stack">
          <NotReady missing={s.missing} />
          <fieldset className="om-set" disabled={!edit}>
            <legend>{t('om.sender')}</legend>
            <div className="form-grid">
              <Field label={t('om.from_name')} hint={t('om.from_name_hint')}>
                <input className="input" value={f.from_name ?? ''} maxLength={80} placeholder={s.sender.name} onChange={(e) => up('from_name', e.target.value)} />
              </Field>
              <Field label={t('om.from_email')} hint={s.marketing_from ? t('om.from_email_hint', { email: s.marketing_from }) : t('om.from_email_hint_none')}>
                <input className="input" type="email" value={f.from_email ?? ''} maxLength={200} placeholder={s.marketing_from ?? 'business@example.com'} onChange={(e) => up('from_email', e.target.value)} />
              </Field>
              <Field label={t('om.reply_to')} hint={t('om.reply_to_hint')}>
                <input className="input" type="email" value={f.reply_to ?? ''} maxLength={200} onChange={(e) => up('reply_to', e.target.value)} />
              </Field>
            </div>
            <Field label={t('om.signature')} hint={t('om.signature_hint')} counter={`${(f.signature ?? '').length}/2000`}>
              <textarea className="input" rows={4} value={f.signature ?? ''} maxLength={2000} onChange={(e) => up('signature', e.target.value)} />
            </Field>
          </fieldset>
          <fieldset className="om-set" disabled={!edit}>
            <legend>{t('om.pace_title')}</legend>
            <p className="muted small">{t('om.pace_intro')}</p>
            <div className="form-grid">
              <Field label={t('om.daily_limit')} hint={t('om.daily_limit_hint')}>
                <input className="input" type="number" inputMode="numeric" min={1} max={1000} value={f.daily_limit} onChange={(e) => up('daily_limit', Math.max(1, Math.min(1000, Number(e.target.value) || 1)))} />
              </Field>
              <Field label={t('om.hourly_limit')} hint={t('om.hourly_limit_hint', { minutes: Math.round(60 / Math.max(1, f.hourly_limit)) })}>
                <input className="input" type="number" inputMode="numeric" min={1} max={120} value={f.hourly_limit} onChange={(e) => up('hourly_limit', Math.max(1, Math.min(120, Number(e.target.value) || 1)))} />
              </Field>
              <Field label={t('om.window_start')}>
                <select className="input" value={f.window_start} onChange={(e) => up('window_start', Number(e.target.value))}>
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>
                      {hour(h)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t('om.window_end')} error={hoursBad ? t('om.e_hours') : null}>
                <select className="input" value={f.window_end} onChange={(e) => up('window_end', Number(e.target.value))}>
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h + 1} value={h + 1}>
                      {hour(h + 1)}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="om-days" role="group" aria-label={t('om.days')}>
              <span className="muted small">{t('om.days')}</span>
              {DAYS.map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`chip${f.weekdays.includes(d) ? ' active' : ''}`}
                  aria-pressed={f.weekdays.includes(d)}
                  onClick={() => up('weekdays', f.weekdays.includes(d) ? f.weekdays.filter((x) => x !== d) : [...f.weekdays, d].sort())}
                >
                  {t(`om.day_${d}` as MessageKey)}
                </button>
              ))}
            </div>
            {!f.weekdays.length ? <p className="field-msg">{t('om.e_days')}</p> : null}
            <label className="out-toggle">
              <Switch checked={f.warmup} onChange={(v) => up('warmup', v)} label={t('om.warmup')} />
              <span>
                {t('om.warmup')}
                <span className="muted small block">{t('om.warmup_hint')}</span>
              </span>
            </label>
            <label className="out-toggle">
              <Switch checked={f.track_opens} onChange={(v) => up('track_opens', v)} label={t('om.track_opens')} />
              <span>
                {t('om.track_opens')}
                <span className="muted small block">{t('om.track_opens_hint')}</span>
              </span>
            </label>
          </fieldset>
          <fieldset className="om-set">
            <legend>{t('om.dns_title')}</legend>
            <p className="muted small">{t('om.dns_intro')}</p>
            <div className="form-grid">
              <Field label={t('om.dkim_selector')} hint={t('om.dkim_selector_hint')}>
                <input className="input" value={f.dkim_selector ?? ''} maxLength={63} disabled={!edit} placeholder="google" onChange={(e) => up('dkim_selector', e.target.value)} />
              </Field>
              <div className="om-dns-go">
                <Button icon="shieldCheck" busy={checking} disabled={!s.sender.email} onClick={() => void check()}>
                  {dns ? t('om.dns_again') : t('om.dns_check')}
                </Button>
              </div>
            </div>
            {dns ? (
              <>
                <p className="muted small">
                  {t('om.dns_for', { domain: dns.domain })}
                  {dns.checked_at ? ` · ${since(dns.checked_at)}` : ''}
                </p>
                <ul className="om-dns">
                  <DnsRow name="SPF" check={dns.spf} detail={dns.spf_record} advice={t(dns.spf === 'warn' ? 'om.spf_warn' : 'om.spf_missing')} />
                  <DnsRow name="DKIM" check={dns.dkim} detail={dns.dkim_selector ? t('om.dkim_found', { selector: dns.dkim_selector }) : null} advice={t(dns.dkim === 'warn' ? 'om.dkim_warn' : 'om.dkim_missing')} />
                  <DnsRow name="DMARC" check={dns.dmarc} detail={dns.dmarc_record} advice={t(dns.dmarc === 'warn' ? 'om.dmarc_warn' : 'om.dmarc_missing')} />
                  <DnsRow name="MX" check={dns.mx} detail={null} advice={t('om.mx_missing')} />
                </ul>
              </>
            ) : null}
          </fieldset>
        </div>
      )}
    </Dialog>
  );
}
